<template>
    <!-- Modified by Martin - 2026-05-13 -->
    <md-button :class="buttonClasses" :disabled="loading || disabled" @click="handleClick">
        <!-- Spinner during loading state -->
        <md-progress-spinner
            v-if="loading"
            :md-diameter="16"
            :md-stroke="2"
            class="md-white lixsa-action-btn__spinner"
            md-mode="indeterminate"
        />
        <!-- Custom icon slot (e.g. SVG) — takes priority over icon prop -->
        <slot v-else-if="$slots.icon" name="icon" />
        <!-- Material Symbols icon (only when not loading and icon prop is set) -->
        <i v-else-if="icon" class="material-symbols-outlined lixsa-action-btn__icon">{{ icon }}</i>
        <!-- Label text -->
        <span class="lixsa-action-btn__label">
            {{ currentLabel }}
        </span>
    </md-button>
</template>

<script>
export default {
    name: 'LixsaActionButton',
    props: {
        /** Visible button text (already i18n-ized by parent) */
        label: {
            type: String,
            required: true,
        },
        /** Alternative text shown during loading (e.g. "Saving..."). If empty, keeps label */
        loadingLabel: {
            type: String,
            default: '',
        },
        /** Material Symbols icon name (e.g. 'save', 'add', 'link'). Empty = no icon */
        icon: {
            type: String,
            default: '',
        },
        /** Loading state — shows spinner and disables button */
        loading: {
            type: Boolean,
            default: false,
        },
        /** Externally disabled (e.g. form validation) */
        disabled: {
            type: Boolean,
            default: false,
        },
        /** Visual variant: 'primary' | 'secondary' | 'danger' */
        variant: {
            type: String,
            default: 'primary',
            validator: (v) => ['primary', 'secondary', 'danger'].includes(v),
        },
        /** Adds md-raised (shadow/elevation) — use true for page actions, false for modal actions */
        raised: {
            type: Boolean,
            default: true,
        },
        /** Full-width button */
        block: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['click'],
    computed: {
        buttonClasses() {
            return [
                'lixsa-action-btn',
                {
                    'md-raised': this.raised,
                    'md-primary': this.variant === 'primary',
                    'md-accent': this.variant === 'danger',
                    'md-block': this.block,
                    'lixsa-action-btn--secondary': this.variant === 'secondary',
                    'lixsa-action-btn--loading': this.loading,
                },
            ];
        },
        currentLabel() {
            if (this.loading && this.loadingLabel) {
                return this.loadingLabel;
            }
            return this.label;
        },
    },
    methods: {
        handleClick() {
            if (!this.loading && !this.disabled) {
                this.$emit('click');
            }
        },
    },
};
</script>

<style scoped lang="scss">
.lixsa-action-btn {
    text-transform: none !important;
    height: 40px !important;
    min-width: 120px !important;
    font-weight: 500 !important;
    font-size: 14px !important;
    letter-spacing: 0.01em !important;
    border-radius: 8px !important;
    transition: all 0.2s ease !important;

    &__icon {
        font-size: 18px !important;
        margin-right: 6px;
        vertical-align: middle;
    }

    &__spinner {
        margin-right: 8px;
    }

    &__label {
        vertical-align: middle;
        line-height: 1;
    }

    // Secondary variant (no md-primary/md-accent — manual styling)
    &--secondary {
        background-color: transparent !important;
        color: #64748b !important;
        border: 1px solid #e2e8f0 !important;

        &:hover {
            background-color: #f8fafc !important;
            border-color: #cbd5e1 !important;
        }

        // Spinner inherits text color instead of white
        ::v-deep .md-progress-spinner svg circle {
            stroke: #64748b !important;
        }
    }

    // Subtle scale-down on loading to give visual feedback
    &--loading {
        opacity: 0.85;
        pointer-events: none;
    }
}
</style>
